if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.buyNowButton = this.querySelector('[data-unbroken-buy-now]');
        if (this.buyNowButton) {
          this.buyNowButton.addEventListener('click', this.onBuyNowHandler.bind(this));
          const syncBuyNow = () => {
            this.buyNowButton.disabled =
              this.variantIdInput.disabled ||
              this.submitButton.disabled ||
              this.submitButton.getAttribute('aria-disabled') === 'true';
          };
          const availabilityObserver = new MutationObserver(syncBuyNow);
          availabilityObserver.observe(this.submitButton, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled'],
          });
          availabilityObserver.observe(this.variantIdInput, {
            attributes: true,
            attributeFilter: ['disabled'],
          });
          syncBuyNow();
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            this.trackKlaviyoAddedToCart(response, quantity, Boolean(this.cart));

            if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async onBuyNowHandler(event) {
        event.preventDefault();
        if (
          !this.buyNowButton ||
          this.buyNowButton.disabled ||
          this.variantIdInput.disabled ||
          this.submitButton.disabled ||
          this.submitButton.getAttribute('aria-disabled') === 'true'
        ) return;

        this.handleErrorMessage();
        const button = this.buyNowButton;
        const originalLabel = button.textContent;
        const originalSubmitDisabled = this.submitButton.disabled;
        let redirecting = false;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = button.dataset.loadingLabel || originalLabel;
        this.submitButton.disabled = true;

        try {
          const formData = new FormData(this.form);
          if (!formData.get('id')) throw new Error(button.dataset.errorLabel);
          const quantity = parseInt(formData.get('quantity'), 10) || 1;
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.headers.Accept = 'application/json';
          config.body = formData;

          const result = await fetch(window.routes.cart_add_url, config);
          const item = await result.json();
          if (!result.ok || item.status) {
            throw new Error(item.description || item.message || button.dataset.errorLabel);
          }
          this.trackKlaviyoAddedToCart(item, quantity, false);
          redirecting = true;
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const checkoutPath = root.endsWith('/') ? root + 'checkout' : root + '/checkout';
          window.location.assign(checkoutPath);
        } catch (error) {
          const message = error && error.message ? error.message : button.dataset.errorLabel;
          this.handleErrorMessage(message);
          this.dispatchCartErrorEvent(message, 'SERVICE_UNAVAILABLE');
        } finally {
          if (!redirecting) {
            button.textContent = originalLabel;
            button.removeAttribute('aria-busy');
            this.submitButton.disabled = originalSubmitDisabled;
            button.disabled = this.variantIdInput.disabled ||
              this.submitButton.disabled ||
              this.submitButton.getAttribute('aria-disabled') === 'true';
          }
        }
      }

      trackKlaviyoAddedToCart(response, quantity, includeEntireCart = true) {
        try {
          const unitPrice = Number(response.final_price ?? response.price ?? 0);
          const lineValue = Number(response.final_line_price ?? unitPrice * quantity ?? 0);
          const productUrl = response.url
            ? new URL(response.url, window.location.origin).href
            : window.location.href;
          const localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const root = localeRoot.endsWith('/') ? localeRoot : localeRoot + '/';
          const variantId = String(response.variant_id || response.id || '');
          const selectedQuantity = Math.max(1, parseInt(quantity, 10) || 1);
          const fallbackCartUrl = /^[0-9]+$/.test(variantId)
            ? new URL(root + 'cart/' + variantId + ':' + selectedQuantity + '?storefront=true', window.location.origin).href
            : new URL(root + 'cart', window.location.origin).href;

          const dispatch = (cartUrl) => {
            window._learnq = window._learnq || [];
            window._learnq.push([
              'track',
              'Added to Cart',
              {
                $value: lineValue / 100,
                ProductName: response.product_title || response.title || '',
                ProductID: String(response.product_id || ''),
                VariantID: variantId,
                VariantTitle: response.variant_title || '',
                SKU: response.sku || '',
                Quantity: selectedQuantity,
                ItemPrice: unitPrice / 100,
                ImageURL: response.image || response.featured_image?.url || '',
                ProductURL: productUrl,
                CartURL: cartUrl,
                Currency: window.Shopify?.currency?.active || '',
              },
            ]);
          };

          // Keep tracking synchronous when buying now or leaving for the cart.
          if (!includeEntireCart) {
            dispatch(fallbackCartUrl);
            return;
          }

          // A Shopify cart permalink restores the selected items on another device.
          fetch(root + 'cart.js', {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          })
            .then((result) => {
              if (!result.ok) throw new Error('Cart lookup failed');
              return result.json();
            })
            .then((cart) => {
              if (!Array.isArray(cart.items) || cart.items.length === 0) {
                dispatch(fallbackCartUrl);
                return;
              }
              const byVariant = new Map();
              for (const line of cart.items) {
                const id = String(line.variant_id || line.id || '');
                const amount = Number(line.quantity);
                if (!/^[0-9]+$/.test(id) || !Number.isInteger(amount) || amount < 1) {
                  dispatch(fallbackCartUrl);
                  return;
                }
                byVariant.set(id, (byVariant.get(id) || 0) + amount);
              }
              const lines = [...byVariant].map(([id, amount]) => id + ':' + amount).join(',');
              const restoreUrl = new URL(root + 'cart/' + lines + '?storefront=true', window.location.origin).href;
              dispatch(restoreUrl);
            })
            .catch(() => dispatch(fallbackCartUrl));
        } catch (error) {
          console.warn('Klaviyo Added to Cart tracking skipped', error);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
